// Client-side, FULLY OFFLINE verification of rental entitlement tokens
// (Phase 2 — the generic verify primitive only; expiry enforcement and
// per-rental issuance come in Phase 3).
//
// This needs nothing but the bundled public key and the token: no network, no
// server, no JWT library. It uses the browser's Web Crypto (crypto.subtle) and
// the RSA-2048 public key bundled at build time (rentalSigningPublicKey.js).
//
// CRITICAL — cross-runtime interop. The token was SIGNED in Node with RS256
// (see functions/lib/rentalSigning.js); we VERIFY it here with Web Crypto. The
// two must line up exactly: algorithm 'RSASSA-PKCS1-v1_5' + SHA-256, base64url
// segment encoding, and an SPKI-PEM public key imported via importKey('spki').
// The round-trip test proves a Node-signed token verifies through this path.

import { RENTAL_SIGNING_PUBLIC_KEY_PEM } from "./rentalSigningPublicKey";

// Decode a base64url string to a Uint8Array using the browser's atob.
function base64urlToBytes(b64url) {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64.length % 4 ? b64 + "=".repeat(4 - (b64.length % 4)) : b64;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// Strip the PEM armour and base64-decode the body to DER bytes, ready for
// importKey('spki', ...).
function pemToDer(pem) {
  const body = pem
    .replace(/-----BEGIN [^-]+-----/, "")
    .replace(/-----END [^-]+-----/, "")
    .replace(/\s+/g, "");
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// Resolve a Web Crypto SubtleCrypto. In the browser this is the global
// crypto.subtle; tests inject one (jsdom does not provide crypto.subtle).
function resolveSubtle(injected) {
  if (injected) return injected;
  if (typeof window !== "undefined" && window.crypto && window.crypto.subtle) {
    return window.crypto.subtle;
  }
  throw new Error("Web Crypto (crypto.subtle) is not available in this environment");
}

// Verify a compact RS256 token against an SPKI-PEM public key, fully offline.
// Returns { valid, payload }: payload is the decoded JSON only when the
// signature is valid, otherwise null. NOTE: this checks the SIGNATURE only —
// it does not interpret the payload, so the caller (Phase 3) is responsible for
// enforcing expiry (expiresAt) and any audiobookId match.
export async function verifyToken(token, publicKeyPem, { subtle } = {}) {
  if (!token || !publicKeyPem) return { valid: false, payload: null };
  const s = resolveSubtle(subtle);

  const parts = token.split(".");
  if (parts.length !== 3) return { valid: false, payload: null };
  const [encodedHeader, encodedPayload, encodedSignature] = parts;
  const signingInput = `${encodedHeader}.${encodedPayload}`;

  let key;
  try {
    key = await s.importKey(
      "spki",
      pemToDer(publicKeyPem),
      { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
      false,
      ["verify"],
    );
  } catch (e) {
    // An empty or malformed public key cannot verify anything — fail closed.
    return { valid: false, payload: null };
  }

  let valid;
  try {
    valid = await s.verify(
      "RSASSA-PKCS1-v1_5",
      key,
      base64urlToBytes(encodedSignature),
      new TextEncoder().encode(signingInput),
    );
  } catch (e) {
    return { valid: false, payload: null };
  }
  if (!valid) return { valid: false, payload: null };

  let payload = null;
  try {
    payload = JSON.parse(new TextDecoder().decode(base64urlToBytes(encodedPayload)));
  } catch (e) {
    return { valid: false, payload: null };
  }
  return { valid: true, payload };
}

// Convenience wrapper that verifies against the bundled platform public key.
// This is what the reader portal / native player will call at playback time.
export async function verifyRentalToken(token, options = {}) {
  return verifyToken(token, RENTAL_SIGNING_PUBLIC_KEY_PEM, options);
}
